import { Component, Prop, Ref, SimpleEvent } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { parseUrl, stringifyUrl } from 'query-string';
import { formatMoney } from '@/utils/formatting';
import { cart } from '@/theme/Cart';
import { CartMain } from "../Cart/CartMain";
import debounce from 'lodash/debounce';
import { SideCart } from '../SideCart/SideCart';

declare global {
    interface Window {

        // eslint-disable-next-line
        quickLaunch?: any;

        // eslint-disable-next-line
        _swat?: any;

        // eslint-disable-next-line @typescript-eslint/naming-convention
        ReloadSmartWishlist?: {
            (): string;
        };
    }
}
@Component
export class ProductCard extends ThemeComponent {
    @Prop product!: Shopify.Liquid.Product;
    @Prop({default: true}) useImagePreload!: boolean;

    @Ref({required: false}) swatches!: HTMLElement[];
    @Ref({required: false}) price!: HTMLElement[];
    @Ref({required: false}) comparePrice!: HTMLElement[];
    @Ref() image!: HTMLImageElement[];
    @Ref() link!: HTMLAnchorElement[];
    @Ref({required: false}) color!: HTMLElement;
    @Ref({required: false}) quickAddContainer!: HTMLElement;
    @Ref({required: false}) quickAddToggle!: HTMLElement;
    @Ref({required: false}) quickAddOptionSelector!: HTMLElement[];
    @Ref({required: false}) quickAddDropdown!: HTMLElement;
    @Ref({required: false}) quickAddSubmit!: HTMLElement;
    @Ref({required: false}) swatchVariants!: HTMLElement[];
    @Ref({required: false}) wishlist!: HTMLButtonElement;

    private cartMain:CartMain = document.body.querySelector('cart-main') as CartMain;
    private sideCart:SideCart|null = document.body.querySelector('side-cart');
    private _onSelectedVariantChange = new SimpleEvent<Shopify.Liquid.Variant|null>();
    public get onSelectedVariantChange() {
        return this._onSelectedVariantChange.asEvent();
    }

    private quickAddSelectedVariantId: number|null = null;
    private windowWidth: number = window.innerWidth;
    private viewPort:string = '';

    /**
     * Initialise.
     */
    init() {
        this.setEventListeners();
        this.viewPort = this.checkWidth(this.windowWidth);
        this.reInitWishlist();
    }

    /**
     * Set event listeners.
     */
    setEventListeners() {
        for (const swatch of this.swatches) {
            this.$listen(swatch, 'click', () => this.handleSwatchPicked(swatch));
        }

        if (!window.quickLaunch.quickAdd) { return; }

        this.handleQuickAddEvents();
        // Select first available variant
        this.quickAddSelectedVariantId = Number(this.quickAddSubmit?.dataset.variant);

        // Toggle container
        if (this.quickAddContainer && this.quickAddToggle) {
            this.$listen(this.quickAddToggle, 'click', () => {
                this.handleToggle();
            });
        }

        if (this.quickAddSubmit) {
            // this.$listen(this.quickAddSubmit, 'click', () => this.handleAddToCart());
        }

        if (this.wishlist) {
            // Wait for Wishlist to load
            setTimeout(()=> {
                this.handleWishlist();
            }, 1500);

            this.$listen(this.wishlist, 'click', () => {
                this.wishlist.disabled = true;
                if (this.wishlist.classList.contains('is-active')) {
                    this.removeFromWishlist();
                    return;
                }

                this.addToWishlist();
            });
        }

        for (const optionSelector of this.quickAddOptionSelector) {
            this.$listen(optionSelector, 'click', () => this.handleOptionSelect(optionSelector));
        }

        window.addEventListener('resize',
            debounce(() => {
                this.handleResize();
            }, 300)
        );
    }

    /**
     * Handle swatch picked.
     * @param swatch - color.
     * @returns
     */
    handleSwatchPicked(swatch: HTMLElement) {
        // Get swatch value (color name)
        const value = swatch.dataset.swatchValue;
        if (!value) {
            this.$log.warn(`Swatch with no value clicked`, { swatch });
            return;
        }

        // Find position of color option
        let colorPosition = -1;
        const possibleColorLabels = [
            'Colour',
            'colour',
            'Color',
            'color'
        ];

        for (const possibleColorLabel of possibleColorLabels) {
            colorPosition = this.product.options.indexOf(possibleColorLabel);
            if (colorPosition !== -1) {
                break;
            }
        }

        if (colorPosition === -1) {
            this.$log.warn(`Could not find color variant option`);
            return;
        }

        // Find variant
        const colorOptionKey = `option${colorPosition + 1}` as ShopifyProductOptionKey;
        const selectedVariant = this.product.variants.filter((v) => v[colorOptionKey] == value)[0];
        if (!selectedVariant) {
            this.$log.warn(`Product variant not found`, { swatch, p: this.product });
            return;
        }

        this._onSelectedVariantChange.dispatch(selectedVariant);

        // Remove selected classes
        for (const swatch of this.swatches) {
            swatch.classList.remove('product-card-swatches__swatch--selected');
        }

        if (this.price) {
            // Update price
            for (const price of this.price) {
                price.innerHTML = formatMoney(selectedVariant.price) as string;
            }

            for (const compare of this.comparePrice) {
                if (compare && selectedVariant.compare_at_price) {
                    compare.innerHTML = formatMoney(selectedVariant.compare_at_price) as string;
                }
            }
        }

        this.updateSwatchImage(selectedVariant);
        this.updateSwatchVariants(value);

        if (this.color) {
            this.color.innerHTML = value;
        }

        // Add selected class
        swatch.classList.add('product-card-swatches__swatch--selected');

        if (!window.quickLaunch.quickAdd) { return; }

        // Update clicked quick add option
        for (const quickAddOptionSelector of this.quickAddOptionSelector) {
            const optionValue = quickAddOptionSelector.getAttribute('data-swatch-value');
            if (optionValue === value) {
                quickAddOptionSelector.click();
                break;
            }
        }
    }

    updateSwatchImage(selectedVariant:Shopify.Liquid.Variant) {
        // Update image
        // eslint-disable-next-line @typescript-eslint/naming-convention
        if (selectedVariant.featured_image?.src) {
            for (const image of this.image) {
                // Clear srcset so the swapped-in variant image is the one shown
                // (a present srcset would override the new src).
                image.srcset = '';
                image.src = selectedVariant.featured_image.src;
            }
        }

        // Get URL
        const href = this.link[0].getAttribute('href') || '';
        const { url, query, fragmentIdentifier } = parseUrl(href);
        const newUrl = stringifyUrl({
            url,
            query: {
                ...query,
                variant: selectedVariant.id,
            },
            fragmentIdentifier
        });

        // Update URL
        for (const link of this.link) {
            link.setAttribute('href', newUrl);
        }
    }

    /**
     * Update swatch variants.
     * @param color - swatch color.
     * @returns
     */
    updateSwatchVariants(color: string) {
        if (this.swatchVariants.length <= 0) { return; }
        const sanitized_color = color.toLowerCase();

        for (const wrapper of this.swatchVariants) {
            wrapper.classList.remove('is-active');
            if (wrapper.dataset.color === sanitized_color) {
                wrapper.classList.add('is-active');
            }
        }
    }

    // Add quick add click event listeners
    handleQuickAddEvents() {
        this.quickAddContainer?.classList.remove('inactive');

        // Dropdown
        if (this.quickAddDropdown) {
            // eslint-disable-next-line max-len
            const triggerElement: HTMLElement|null = this.quickAddDropdown.querySelector('.quick-add-dropdown__selected-value');
            if (triggerElement) {
                this.$listen(triggerElement, 'click', () => {
                    this.quickAddDropdown.classList.toggle('quick-add-dropdown--active');
                    if (this.quickAddContainer) {
                        this.quickAddContainer.classList.toggle('product-quick-add--dropdown-active');
                    }
                });
            }
        }

        for (const optionSelector of this.quickAddOptionSelector) {
            this.$listen(optionSelector, 'click', () => this.handleOptionSelect(optionSelector));
        }
    }

    /**
     * Handle option selection.
     * @param optionSelector
     */
    handleOptionSelect(optionSelector:HTMLElement) {
        // Remove selected class from other options
        for (const otherOptionSelector of this.quickAddOptionSelector) {
            const parent = otherOptionSelector.closest('.product-quick-add__option-container');
            parent?.classList.remove('product-quick-add__option-container--selected');
            otherOptionSelector.classList.remove('is-active');
        }

        // Add selected class to this option
        const parent = optionSelector.closest('.product-quick-add__option-container');
        parent?.classList.add('product-quick-add__option-container--selected');

        // Get the selected variant ID
        const selectedVariantIdAttribute = optionSelector.getAttribute('data-variant-id');

        // Check the attribute was returned
        if (selectedVariantIdAttribute && selectedVariantIdAttribute != '') {

            // Update the selected variant ID
            const selectedVariantId = parseInt(selectedVariantIdAttribute, 10);
            this.quickAddSelectedVariantId = selectedVariantId;

            // Remove disabled class from the submit button
            if (this.quickAddSubmit) {
                if (optionSelector.dataset.available === 'false') {
                    this.quickAddSubmit.classList.add('button--disabled');
                } else {
                    this.quickAddSubmit.classList.remove('button--disabled');
                }
            }
        } else {
            // Update the selected variant ID
            this.quickAddSelectedVariantId = null;

            // Add disabled class to the submit button
            if (this.quickAddSubmit) {
                this.quickAddSubmit.classList.add('button--disabled');
            }
        }

        this.dropdownUpdate(optionSelector);
        this.swatchUpdate(optionSelector);
    }

    /**
     * Update dropdown state.
     * @param optionSelector
     */
    dropdownUpdate(optionSelector:HTMLElement) {
        if (this.quickAddDropdown) {
            const optionValue = optionSelector.textContent;
            // eslint-disable-next-line max-len
            const dropdownSelectedValueElement: HTMLElement|null = this.quickAddDropdown.querySelector('[js-selected-value]');
            if (dropdownSelectedValueElement) {
                dropdownSelectedValueElement.textContent = optionValue;
            }
            optionSelector.classList.add('is-active');

            this.quickAddDropdown.classList.remove('quick-add-dropdown--active');
            if (this.quickAddContainer) {
                this.quickAddContainer.classList.remove('product-quick-add--dropdown-active');
            }

            // auto add to cart on variant select -- desktop.
            if (this.dataset.autoAdd) {
                const viewPort = this.checkWidth(window.innerWidth);
                if(viewPort === 'isDesktop') {
                    // this.handleAddToCart().catch((err) => {
                    //     this.$log.error('Quick Add', {err});
                    // });
                }
            }
        }
    }

    /**
     * Update clicked swatch.
     * @param optionSelector
     */
    swatchUpdate(optionSelector:HTMLElement) {
        const optionName = optionSelector.getAttribute('data-option-name');
        const dropdownSelectedValueElement: HTMLElement|null = this.querySelector('[js-selected-value]');

        if (optionName && (optionName == 'color' || optionName == 'colour')) {
            const optionValue = optionSelector.getAttribute('data-swatch-value');
            for (const swatch of this.swatches) {
                const swatchValue = swatch.getAttribute('data-swatch-value');
                if (optionValue === swatchValue) {
                    if (dropdownSelectedValueElement) {
                        dropdownSelectedValueElement.textContent = optionValue;
                    }
                    swatch.click();
                    break;
                }
            }
        }
    }

    /**
     *
     * @returns Handle Add to Cart.
     */
    // async handleAddToCart() {
    //     // Check that we have a selected variant ID
    //     if (this.quickAddSelectedVariantId === null) {
    //         this.$log.error(`Quick Add failed - selected variant ID was null`);
    //         return;
    //     }

    //     const itemInCart:boolean = this.cartMain.checkCart(this.quickAddSelectedVariantId);

    //     // Add to cart
    //     cart.addItem({
    //         id: this.quickAddSelectedVariantId,
    //         quantity: 1
    //     }).then((item) => {
    //         if (itemInCart) {
    //             this.cartMain.updateCart('update', item, item.quantity);

    //             if (!this.sideCart) { return; }
    //             this.sideCart.openCart();
    //         } else {
    //             this.cartMain.updateCart('add', item);
    //         }
    //     }).catch((event: unknown | null) => {
    //         if (event instanceof Error) {
    //             this.$log.error(event.message);
    //         }
    //     });
    // }

    /**
     * Handle quick add Toggle.
     */
    handleToggle() {
        if (this.quickAddContainer.classList.contains('product-quick-add--active')) {
            this.quickAddContainer.classList.remove('product-quick-add--active');
            document.body.classList.remove('drawer-open');
            this.closest('.swiper')?.classList.remove('no-transform');
            document.dispatchEvent(new Event('closeSizeDrawer'));
            return;
        }

        this.quickAddContainer.classList.add('product-quick-add--active');

        if (window.innerWidth < 768) {
            document.body.classList.add('drawer-open');
            this.closest('.swiper')?.classList.add('no-transform');
        }
    }

    /**
     * Handle window resize.
     */
    handleResize() {
        const newViewPort = this.checkWidth(window.innerWidth);
        if (newViewPort === this.viewPort) { return; }

        this.viewPort = newViewPort;
        if(this.quickAddContainer) {
            this.quickAddContainer.classList.remove('product-quick-add--active');
            document.body.classList.remove('drawer-open');
        }
    }

    /**
     * Cheheck window width.
     * @param width
     * @returns
     */
    checkWidth(width:number) {
        if (width > 768) {
            return 'isDesktop';
        }

        return 'isMobile';
    }

    /**
     * Handle Wishlist.
     */
    handleWishlist() {
        /* eslint-disable */
        if (!window._swat) { return; }
        const productId =  this.product.id;

        window._swat.fetch((items: {[key: string]: string}[]) => {
            for (const item of items) {
                if (~~productId === ~~item.empi) {
                    this.wishlist.classList.add('is-active');
                }
            }
        });
        /* eslint-enable */
    }

    /**
     * Add to wishlist.
     * @returns
     */
    addToWishlist() {
        /* eslint-disable */
        if (!window._swat) { return; }
        const variantId = Number(this.wishlist.dataset.selected);
        const productId = this.product.id;
        const productUrl = this.link[0].href;
        const image = this.product.featured_image;
        const quantity = this.wishlist.dataset.quantity;
        const variants = this.product.variants;
        const $this = this;

        window._swat.addToWishList(
            {
              "epi": variantId,
              "du": productUrl,
              "empi": productId,
              "iu" : image,
              "pr": this.product.price,
              "stk": quantity,
              "et":4,
              "variants": variants
            },
            function() {
                $this.wishlist.classList.add('is-active');
                $this.wishlist.disabled = false;
            }
        );

        /* eslint-enable */
    }

    /**
     * Remove from wishlist.
     * @returns
     */
    removeFromWishlist() {
        /* eslint-disable */
        if (!window._swat) { return; }
        const variantId = Number(this.wishlist.dataset.selected);
        const productId = this.product.id;
        const productUrl = this.link[0].href;
        const image = this.product.featured_image;
        const quantity = this.wishlist.dataset.quantity;
        const variants = this.product.variants;
        const $this = this;

        window._swat.removeFromList(
            {
              "epi": variantId,
              "du": productUrl,
              "empi": productId,
              "iu" : image,
              "pr": this.product.price,
              "stk": quantity,
              "variants": variants
            },
            function() {
                $this.wishlist.classList.remove('is-active');
                $this.wishlist.disabled = false;
            }
        );

        /* eslint-enable */
    }

    /**
     * Reinit wishlist
     * Useful for when cards are dyamically loaded via JS
     * @returns
     */
    reInitWishlist() {
        if (typeof window.ReloadSmartWishlist !== 'undefined') {
            window.ReloadSmartWishlist();
        }
    }
}
